import { i18n } from "@/portal/Trans";

export interface IPasswordRule {
    msg: string;
    validate: { (str: string): boolean };
}

export class PasswordInput {
    private $element: JQuery;
    private $formCtrl: JQuery;
    private $input: JQuery;
    private $showBtn: JQuery;
    private $strength?: JQuery;
    private $strengthBar?: JQuery;
    private $strengthMsg?: JQuery;

    private rules?: Array<IPasswordRule>;

    constructor(showStrength: boolean, intent: boolean, label: string) {
        this.$element = $('<div class="pwinput" />');
        this.$formCtrl = $(
            '<div class="formcontrol' + (intent === true ? " intent" : "") + '"><label>' + label + "</label></div>"
        ).appendTo(this.$element);
        this.$input = $('<input type="password" name="password" autocomplete="off" />').appendTo(this.$formCtrl);

        this.$showBtn = $('<div class="showbtn"><i class="fal fa-eye"></i></div>').appendTo(this.$formCtrl);

        if (showStrength) {
            this.rules = new Array<IPasswordRule>();

            this.rules.push({
                msg: i18n("elements.passwordInput.minLength", { count: 8 }),
                validate: (s) => {
                    return s.length >= 8;
                },
            });

            this.rules.push({
                msg: i18n("elements.passwordInput.minUpperCase", { count: 1 }),
                validate: (s) => {
                    return /[A-Z]/.test(s);
                },
            });

            this.rules.push({
                msg: i18n("elements.passwordInput.minNumbers", { count: 1 }),
                validate: (s) => {
                    return /[0-9]/.test(s);
                },
            });

            this.rules.push({
                msg: i18n("elements.passwordInput.minLowerCase", { count: 1 }),
                validate: (s) => {
                    return /[a-z]/.test(s);
                },
            });

            this.$input
                .unbind("keyup.s")
                .bind("keyup.s", () => {
                    this.checkStrength();
                })
                .unbind("change.s")
                .bind("change.s", () => {
                    this.checkStrength();
                });

            this.$strength = $('<div class="strength' + (intent === true ? " intent" : "") + '" />').appendTo(
                this.$element
            );
            const indicator = $('<div class="indicator" />').appendTo(this.$strength);
            this.$strengthBar = $('<div class="bar" />').appendTo(indicator);
            this.$strengthMsg = $('<div class="msg" />').appendTo(this.$strength);

            this.checkStrength();
        }

        this.$showBtn.click(() => {
            if (this.$showBtn.hasClass("isactive")) this.displayAsPlainText(false);
            else this.displayAsPlainText(true);
        });
    }

    public onKeyUp(handler: { (nv: string) }) {
        this.$input
            .unbind("keyup.b")
            .bind("keyup.b", () => {
                handler(this.getValue());
            })
            .unbind("change.b")
            .bind("change.b", () => {
                handler(this.getValue());
            });
    }

    public isValid(): boolean {
        const pw = this.getValue();
        if (pw === undefined) return false;

        for (const i in this.rules) {
            if (!this.rules[i].validate(pw)) return false;
        }

        return true;
    }

    public getValue(): string {
        return this.$input.val().length === 0 ? undefined : this.$input.val();
    }

    public setValue(val?: string): PasswordInput {
        if (val === undefined || val === "") {
            this.$formCtrl.removeClass("edited");
            this.$input.val("");
        } else this.$input.val(val);

        return this;
    }

    public setError(hasError: boolean): PasswordInput {
        if (hasError) this.$formCtrl.addClass("has-error");
        else this.$formCtrl.removeClass("has-error");

        return this;
    }

    public appendTo($element: JQuery): PasswordInput {
        this.$element.appendTo($element);
        return this;
    }

    private displayAsPlainText(isPlainText: boolean) {
        if (isPlainText) this.$showBtn.addClass("isactive");
        else this.$showBtn.removeClass("isactive");

        this.$input.attr("type", isPlainText ? "text" : "password");
    }

    private checkStrength() {
        const currentPw = this.$input.val();

        let passed = 0;
        let msg = "";
        for (const i in this.rules) {
            const rule = this.rules[i];
            if (rule.validate(currentPw)) passed++;
            else {
                if (msg === "") msg = rule.msg;
            }
        }

        let className = "";
        const passedPercent = (100 / this.rules!.length) * passed;
        if (passedPercent <= 25) className = "bad";
        else if (passedPercent <= 50) className = "medium";
        else if (passedPercent <= 75) className = "close";
        else className = "good";

        if (passedPercent >= 100) this.$strength!.addClass("resolved");
        else this.$strength!.removeClass("resolved");

        this.$strengthBar!.width(passedPercent + "%")
            .removeClass("bad medium close good")
            .addClass(className);
        this.$strengthMsg!.html(msg);
    }
}
